import React, { useState } from 'react';
import Lock from 'lucide-react/dist/esm/icons/lock';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import Eye from 'lucide-react/dist/esm/icons/eye';
import EyeOff from 'lucide-react/dist/esm/icons/eye-off';
import { setNewPassword } from '../services/auth';
import { getTranslation } from '../../../i18n';

const NewPasswordForm = ({ email, session, onSuccess, language = 'en' }) => {
  const [newPassword, setNewPasswordValue] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');

  const t = (key) => getTranslation(language, key);

  const calculatePasswordStrength = (pwd) => {
    if (!pwd) return '';
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (pwd.length >= 12) strength++;
    if (/[a-z]/.test(pwd)) strength++;
    if (/[A-Z]/.test(pwd)) strength++;
    if (/\d/.test(pwd)) strength++;
    if (/[^a-zA-Z\d]/.test(pwd)) strength++;
    if (strength <= 2) return 'weak';
    if (strength <= 4) return 'medium';
    return 'strong';
  };

  const handlePasswordChange = (value) => {
    setNewPasswordValue(value);
    setPasswordStrength(calculatePasswordStrength(value));
  };

  const handleKeyPress = (e) => {
    const isCapsLock = e.getModifierState && e.getModifierState('CapsLock');
    setCapsLockOn(isCapsLock);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError(t('passwordsDoNotMatch'));
      return;
    }

    if (newPassword.length < 8) {
      setError(t('passwordTooShort'));
      return;
    }

    setLoading(true);

    const result = await setNewPassword(email, newPassword, session);

    if (result.success) {
      onSuccess();
    } else {
      setError(result.error || t('failedToSetPassword'));
    }

    setLoading(false);
  };

  const strengthColors = {
    weak: 'bg-gradient-to-r from-red-500 to-red-600',
    medium: 'bg-gradient-to-r from-amber-500 to-amber-600',
    strong: 'bg-gradient-to-r from-green-500 to-green-600'
  };

  const strengthTextColors = {
    weak: 'text-status-error',
    medium: 'text-amber-500',
    strong: 'text-green-500'
  };

  const strengthWidths = {
    weak: 'w-1/3',
    medium: 'w-2/3',
    strong: 'w-full'
  };

  return (
    <div className="relative h-full w-full flex justify-center items-center p-8">
      {/* Background glow effect */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none animate-pulse"
        style={{ background: 'radial-gradient(circle, rgba(33, 158, 188, 0.15) 0%, transparent 70%)' }}
      />

      <div className="relative z-10 w-full max-w-[450px] px-10 py-7 rounded-2xl border border-accent bg-glass-strong backdrop-blur-2xl shadow-xl-dark animate-fade-in">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-7">
          <div className="relative mb-5">
            <img src="/quazi-logo.svg" alt="QUAZI Logo" className="w-[72px] h-auto drop-shadow-[0_0_20px_rgba(33,158,188,0.7)]" />
            <Sparkles className="absolute -top-2 -right-2 text-brand-light animate-pulse" size={24} />
          </div>
          <h1 className="font-display text-5xl font-bold tracking-[0.15em] mb-2.5 leading-tight gradient-text">QUAZI</h1>
          <p className="text-lg text-secondary font-medium tracking-wider uppercase mb-4 leading-normal">{t('setNewPassword')}</p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand/20 border border-brand rounded-xl text-brand-light text-[0.6875rem] font-semibold uppercase tracking-wider">
            <Lock size={14} />
            <span>GSI Secure System</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
          <p className="text-[0.9375rem] text-secondary text-center mb-6 leading-relaxed">{t('tempPasswordMessage')}</p>

          <div className="relative flex items-center">
            <Lock size={20} className="absolute left-5 text-tertiary pointer-events-none z-10" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder={t('newPassword')}
              value={newPassword}
              onChange={(e) => handlePasswordChange(e.target.value)}
              onKeyDown={handleKeyPress}
              onKeyUp={handleKeyPress}
              className="w-full py-4 pr-12 pl-[3.25rem] bg-surface-elevated border border-medium rounded-xl text-content text-lg font-normal transition-all duration-250 placeholder:text-tertiary focus:outline-none focus:border-brand focus:bg-surface-secondary focus:shadow-[0_0_0_3px_rgba(33,158,188,0.1)]"
              required
              autoComplete="new-password"
            />
            {capsLockOn && (
              <div className="absolute top-1/2 right-14 -translate-y-1/2 bg-status-error/10 border border-status-error rounded-lg px-2.5 py-1.5 text-xs text-status-error-light font-medium whitespace-nowrap z-10 animate-fade-in">
                Caps Lock
              </div>
            )}
            <button
              type="button"
              className="absolute right-5 text-tertiary cursor-pointer z-10 p-1 transition-colors duration-250 hover:text-primary"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          {newPassword && (
            <div className="-mt-1 mb-2">
              <div className="text-[0.8125rem] text-tertiary mb-1.5 flex items-center gap-2">
                {t('passwordStrength')}
                <span className={`font-semibold ${strengthTextColors[passwordStrength]}`}>
                  {t(passwordStrength)}
                </span>
              </div>
              <div className="h-1 bg-surface-elevated rounded-sm overflow-hidden">
                <div className={`h-full transition-all duration-300 rounded-sm ${strengthWidths[passwordStrength]} ${strengthColors[passwordStrength]}`} />
              </div>
            </div>
          )}

          <div className="relative flex items-center">
            <Lock size={20} className="absolute left-5 text-tertiary pointer-events-none z-10" />
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder={t('confirmNewPassword')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyDown={handleKeyPress}
              onKeyUp={handleKeyPress}
              className="w-full py-4 pr-12 pl-[3.25rem] bg-surface-elevated border border-medium rounded-xl text-content text-lg font-normal transition-all duration-250 placeholder:text-tertiary focus:outline-none focus:border-brand focus:bg-surface-secondary focus:shadow-[0_0_0_3px_rgba(33,158,188,0.1)]"
              required
              autoComplete="new-password"
            />
            {capsLockOn && (
              <div className="absolute top-1/2 right-14 -translate-y-1/2 bg-status-error/10 border border-status-error rounded-lg px-2.5 py-1.5 text-xs text-status-error-light font-medium whitespace-nowrap z-10 animate-fade-in">
                Caps Lock
              </div>
            )}
            <button
              type="button"
              className="absolute right-5 text-tertiary cursor-pointer z-10 p-1 transition-colors duration-250 hover:text-primary"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <p className="text-[0.8125rem] text-tertiary text-center my-2 leading-relaxed italic">{t('passwordRequirements')}</p>

          {error && (
            <div className="p-4 bg-status-error/10 border border-status-error rounded-xl text-status-error-light text-base font-medium text-center leading-normal animate-fade-in">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="relative flex items-center justify-center gap-3 w-full py-4 px-6 bg-gradient-to-br from-brand to-brand-hover border-none rounded-xl text-content-inverse text-lg font-semibold cursor-pointer uppercase tracking-widest shadow-glow mt-4 overflow-hidden transition-all duration-250 hover:translate-y-[-3px] hover:shadow-glow-strong active:translate-y-[-1px] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 group"
            disabled={loading}
          >
            <span className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-[left] duration-500 group-hover:left-full" />
            {loading ? (
              <>
                <span className="w-5 h-5 border-2 border-content/30 border-t-content rounded-full animate-spin" />
                {t('settingPassword')}
              </>
            ) : (
              t('setPassword')
            )}
          </button>
        </form>

        <div className="mt-4 text-center pt-4 border-t border-subtle">
          <p className="text-[0.6875rem] text-secondary font-medium m-0">🇲🇽 A product of GSI Mexico</p>
        </div>
      </div>
    </div>
  );
};

export default NewPasswordForm;
